import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { getPractice } from "../../api";

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002";

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
};
const quillFormats = ["bold", "italic", "underline", "list", "bullet", "align", "link"];

function formatDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ViewIntakeSettings({ refresh }) {
  const [practices, setPractices] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [practice, setPractice] = useState(null);
  const [editing, setEditing] = useState(false);
  const [termsHtml, setTermsHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const token = sessionStorage.getItem("user");
      if (!token) { setLoading(false); return; }
      const decoded = jwtDecode(token);
      const allPrac = await getPractice();
      const filtered = (allPrac || []).filter((p) =>
        (decoded.practice || []).includes(p.practiceNumber)
      ).sort((a, b) => {
        if (a.holdingPractice === "true" && b.holdingPractice !== "true") return -1;
        if (a.holdingPractice !== "true" && b.holdingPractice === "true") return 1;
        return (a.practiceName || "").localeCompare(b.practiceName || "");
      });
      setPractices(filtered);
      if (filtered.length > 0) {
        const first = filtered[0];
        setSelectedId(first._id);
        setPractice(first);
      }
      setLoading(false);
    }
    load();
  }, [refresh]);

  function handlePracticeSelect(id) {
    setSelectedId(id);
    setEditing(false);
    const found = practices.find((p) => p._id === id);
    setPractice(found || null);
  }

  async function handleSave() {
    if (!practice) return;
    setSaving(true);
    try {
      const token = sessionStorage.getItem("user");
      const decoded = token ? jwtDecode(token) : {};
      const updatedSettings = {
        termsAndConditions: termsHtml,
        updatedAt: new Date().toISOString(),
        updatedBy: `${decoded.firstName || ""} ${decoded.lastName || ""}`.trim() || decoded.email || "",
      };
      await axios.put(`${URL}/practice/${practice._id}`, {
        intakeSettings: updatedSettings,
      });
      // Refresh the local practice object
      setPractice((prev) => ({ ...prev, intakeSettings: updatedSettings }));
      setEditing(false);
    } catch (err) {
      console.error("Failed to save intake settings:", err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading…
      </Container>
    );
  }

  return (
    <Container className="py-3">
      <Row className="mb-3">
        <Col md={4}>
          <Form.Label className="fw-semibold">Practice</Form.Label>
          <Form.Select
            value={selectedId}
            onChange={(e) => handlePracticeSelect(e.target.value)}
            disabled={practices.length <= 1}
          >
            {practices.map((p) => (
              <option key={p._id} value={p._id}>
                {p.practiceName || p.practiceNumber}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Terms &amp; conditions are configured per practice.
          </Form.Text>
        </Col>
      </Row>

      {practice && (
        <div className="bg-white rounded shadow-sm p-4">
          <h6 className="fw-semibold mb-3 text-primary">
            Terms &amp; Conditions — {practice.practiceName || practice.practiceNumber}
          </h6>

          {!editing ? (
            practice.intakeSettings?.termsAndConditions ? (
              <>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="badge bg-success">Active</span>
                  {practice.intakeSettings.updatedAt && (
                    <small className="text-muted">
                      Last updated: {formatDate(practice.intakeSettings.updatedAt)}
                      {practice.intakeSettings.updatedBy ? ` by ${practice.intakeSettings.updatedBy}` : ""}
                    </small>
                  )}
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="ms-auto"
                    onClick={() => {
                      setTermsHtml(practice.intakeSettings.termsAndConditions);
                      setEditing(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
                <div
                  className="border rounded p-3 bg-light small"
                  style={{ maxHeight: 360, overflowY: "auto" }}
                  dangerouslySetInnerHTML={{ __html: practice.intakeSettings.termsAndConditions }}
                />
              </>
            ) : (
              <Alert variant="warning" className="d-flex align-items-center gap-3">
                <div>
                  <strong>No terms and conditions configured.</strong> The intake send button will not
                  appear on appointments until you add terms and conditions for this practice.
                </div>
                <Button
                  size="sm"
                  variant="warning"
                  className="ms-auto flex-shrink-0"
                  onClick={() => { setTermsHtml(""); setEditing(true); }}
                >
                  Set Up
                </Button>
              </Alert>
            )
          ) : (
            <>
              <ReactQuill
                theme="snow"
                value={termsHtml}
                onChange={setTermsHtml}
                modules={quillModules}
                formats={quillFormats}
                style={{ minHeight: 200 }}
              />
              <div className="d-flex gap-2 mt-3">
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
                <Button variant="outline-secondary" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Container>
  );
}
